import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import { SidebarContext } from './SidebarContext';
import Sidebar from './Sidebar';

const StatusRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedStatus } = location.state || {};
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);

  useEffect(() => {
    const fetchRequestsByStatus = async () => {
      if (!selectedStatus) {
        setError('No status selected');
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${ApiURL}/getWorkerRequests.php`);
        const allRequests = response.data || [];
        const filtered = allRequests.filter(
          (req) => req.status.toLowerCase() === selectedStatus.toLowerCase()
        );
        setRequests(filtered);
        setLoading(false);
        setError(null);
      } catch (err) {
        setError(`Failed to load ${selectedStatus} requests: ${err.message}`);
        setLoading(false);
        console.error(`Error fetching ${selectedStatus} requests:`, err.message);
      }
    };

    fetchRequestsByStatus();
  }, [selectedStatus]);

  const formatCurrency = (value) => {
    return value != null ? `₹${Number(value).toLocaleString('en-IN')}` : '₹0';
  };

  const handleRowClick = (requestId) => {
    if (!requestId) {
      console.warn('Invalid requestId:', requestId);
      return;
    }
    const viewedIds = JSON.parse(localStorage.getItem('viewedRequestIds') || '[]');
    if (!viewedIds.includes(requestId)) {
      viewedIds.push(requestId);
      localStorage.setItem('viewedRequestIds', JSON.stringify(viewedIds));
      console.log('Marked request as viewed:', requestId);
    }
    navigate('/RequestReport', { state: { highlightedRequestId: requestId } });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 to-blue-100 pt-16 font-sans">
      <Navbar />
      <div className="flex flex-1 relative">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`flex-1 min-h-screen p-2 sm:p-3 overflow-y-auto ${
            isSidebarOpen ? 'sm:ml-64' : 'ml-0'
          } transition-margin duration-200`}
        >
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h1 className="text-2xl sm:text-4xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-500 mx-auto py-4">
                {selectedStatus
                  ? `${selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)} REQUESTS`
                  : 'REQUEST STATUS'}
              </h1>
              <div className="w-24"></div>
            </div>

            <div className="mt-6">
              {loading ? (
                <div className="text-center py-8 text-blue-600 text-[10px] sm:text-sm">
                  Loading requests...
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-600 text-[10px] sm:text-sm">
                  Error: {error}
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-8 text-blue-600 text-[10px] sm:text-sm">
                  No {selectedStatus || 'selected'} requests found.
                </div>
              ) : (
                <div className="relative overflow-x-auto w-full">
                  <table className="w-full text-[10px] sm:text-xs text-left text-gray-700 shadow-md rounded-lg overflow-hidden">
                    <thead className="text-[10px] sm:text-xs uppercase bg-blue-200 text-black sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-1.5 sticky left-0 bg-blue-200 z-20">Request ID</th>
                        <th className="px-2 py-1.5">Worker Name</th>
                        <th className="px-2 py-1.5">Amount</th>
                        <th className="px-2 py-1.5">Purpose</th>
                        <th className="px-2 py-1.5">Status</th>
                        <th className="px-2 py-1.5">Rejection Reason</th>
                        <th className="px-2 py-1.5">Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((request) => (
                        <tr
                          key={request.id}
                          className="bg-white border-b hover:bg-blue-50 transition-colors duration-200"
                          onClick={() => handleRowClick(request.id)}
                        >
                          <td className="px-2 py-1.5 sticky left-0 bg-white z-10 font-medium text-blue-600">
                            {request.id}
                          </td>
                          <td className="px-2 py-1.5">{request.user_name || 'Unknown'}</td>
                          <td className="px-2 py-1.5">{formatCurrency(request.amount)}</td>
                          <td className="px-2 py-1.5">{request.purpose || 'No purpose'}</td>
                          <td className="px-2 py-1.5">
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </td>
                          <td className="px-2 py-1.5">{request.rejection_reason || '-'}</td>
                          <td className="px-2 py-1.5">
                            {new Date(request.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="sm:hidden absolute top-0 right-0 h-full w-3 bg-gradient-to-l from-gray-200/50 to-transparent pointer-events-none"></div>
                </div>
              )}

              {error && (
                <div className="mt-4 px-4 py-2 bg-red-100 text-red-600 rounded text-[10px] sm:text-sm max-w-4xl mx-auto">
                  {error}
                </div>
              )}
            </div>
          </div>
        </motion.main>
        {isSidebarOpen && screenWidth < 640 && (
          <div
            className="fixed inset-0 bg-black/50 z-30"
            onClick={() => document.querySelector('button[aria-label="Close Sidebar"]').click()}
            aria-hidden="true"
          />
        )}
      </div>

      <style jsx>{`
        .overflow-x-auto {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        @media (max-width: 480px) {
          .overflow-x-auto {
            max-width: 100vw;
          }
          table {
            min-width: 700px;
            width: auto;
          }
        }
        main {
          min-width: 0;
          flex: 1 1 auto;
        }
      `}</style>
    </div>
  );
};

export default StatusRequestsPage;